import { motion } from 'framer-motion';
import { Check, Mail } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newsletterService } from '@/api/services/newsletter';
import { Button } from '@/components/ui';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NewsletterForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!EMAIL_REGEX.test(email)) {
      setErrorMessage(t('newsletter.invalidEmail'));
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      await newsletterService.subscribe({ email });
      setStatus('success');
    } catch (error: unknown) {
      setStatus('error');
      const apiError = error as { message?: string; statusCode?: number };
      if (apiError.statusCode === 409) {
        setErrorMessage(t('newsletter.alreadySubscribed'));
      } else {
        setErrorMessage(t('newsletter.error'));
      }
    }
  };

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-3 px-6 py-4 bg-[#F4F2F2] rounded-full"
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#E94E1B]/10">
          <Check className="w-4 h-4 text-[#E94E1B]" />
        </div>
        <p className="text-[#2E2A23] font-medium text-sm">{t('newsletter.success')}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-md space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888185]" />
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === 'error') {
                setStatus('idle');
                setErrorMessage('');
              }
            }}
            placeholder={t('newsletter.placeholder')}
            aria-label={t('newsletter.placeholder')}
            aria-describedby={errorMessage ? 'newsletter-error' : undefined}
            className="flex h-12 w-full rounded-full border bg-white pl-10 pr-4 py-2 text-base text-[#2E2A23] transition-colors placeholder:text-[#888185] focus:outline-none focus:ring-2 focus:ring-[#E94E1B]/20 focus:border-[#E94E1B] border-[#CAC7C7]"
            disabled={status === 'loading'}
          />
        </div>
        <Button
          type="submit"
          size="lg"
          className="rounded-full whitespace-nowrap"
          isLoading={status === 'loading'}
          disabled={!email}
        >
          {status === 'loading' ? t('newsletter.subscribing') : t('newsletter.subscribe')}
        </Button>
      </div>
      {errorMessage && (
        <motion.p
          id="newsletter-error"
          role="alert"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-500 pl-4"
        >
          {errorMessage}
        </motion.p>
      )}
    </form>
  );
}
